/**
 * Best times to post, computed server-side from the accounts' own history
 * (see server/src/services/bestTimesService.ts). Used by the analytics
 * heatmap, the queue editor and the composer's time suggestions.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { nowZoned, zonedDateToInput } from './timezones';

export type BestTimes = {
  timezone: string;
  source: 'own' | 'mixed' | 'general';
  postCount: number;
  platforms: string[];
  /** grid[dayOfWeek 0=Sun][hour] → 0–100 */
  grid: number[][];
  bestDays: Array<{ dayOfWeek: number; score: number }>;
  bestHours: Array<{ hour: number; score: number }>;
};

export const DAY_NAMES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/**
 * @param accountIds accounts to compute for; undefined = the whole workspace.
 *   An empty array disables the query.
 */
export function useBestTimes(accountIds?: string[], enabled = true) {
  const key = accountIds ? [...accountIds].sort().join(',') : 'all';
  return useQuery({
    queryKey: ['best-times', key],
    queryFn: () =>
      api<BestTimes>(`/api/queue/best-times${accountIds ? `?accountIds=${key}` : ''}`),
    enabled: enabled && (!accountIds || accountIds.length > 0),
    staleTime: 30 * 60 * 1000,
  });
}

/** Same, for the analytics page (which only needs the analytics section). */
export function useBestTimesForAnalytics(accountId?: string) {
  return useQuery({
    queryKey: ['best-times', 'analytics', accountId ?? 'all'],
    queryFn: () =>
      api<BestTimes>(`/api/analytics/best-times${accountId ? `?accountIds=${accountId}` : ''}`),
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * The best upcoming times in the next 7 days, at least 2 h apart, in
 * chronological order — as "YYYY-MM-DDTHH:mm" wall-clock values in `tz`.
 */
export function upcomingBestTimes(bt: BestTimes, tz: string, count = 3): string[] {
  const now = nowZoned(tz);
  const earliest = now.getTime() + 30 * 60_000;
  const candidates: Array<{ at: Date; score: number }> = [];
  for (let offset = 0; offset < 7; offset++) {
    for (let hour = 7; hour <= 23; hour++) {
      const at = new Date(now);
      at.setUTCDate(at.getUTCDate() + offset);
      at.setUTCHours(hour, 0, 0, 0);
      if (at.getTime() < earliest) continue;
      candidates.push({ at, score: bt.grid[at.getUTCDay()]?.[hour] ?? 0 });
    }
  }
  candidates.sort((a, b) => b.score - a.score || a.at.getTime() - b.at.getTime());
  const picked: Date[] = [];
  for (const c of candidates) {
    if (picked.length === count) break;
    if (picked.some((p) => Math.abs(p.getTime() - c.at.getTime()) < 2 * 3_600_000)) continue;
    picked.push(c.at);
  }
  return picked.sort((a, b) => a.getTime() - b.getTime()).map(zonedDateToInput);
}

/** The `perDay` best hours of a weekday, at least 2 h apart, in order. */
export function bestHoursOfDay(bt: BestTimes, dayOfWeek: number, perDay: number): number[] {
  const row = bt.grid[dayOfWeek] ?? [];
  const hours: number[] = [];
  for (const h of [...row.keys()].sort((a, b) => row[b]! - row[a]!)) {
    if (hours.length === perDay) break;
    if (h < 7) continue;
    if (hours.some((x) => Math.abs(x - h) < 2)) continue;
    hours.push(h);
  }
  return hours.sort((a, b) => a - b);
}

/** One-line explanation of where the numbers come from. */
export function bestTimesSourceNote(bt: BestTimes): string {
  if (bt.source === 'own') {
    return `Calculado con tus ${bt.postCount} publicaciones de los últimos 12 meses, en tu zona horaria.`;
  }
  if (bt.source === 'mixed') {
    return `Aún tienes pocas publicaciones (${bt.postCount}): mezclamos tus resultados con los horarios que suelen funcionar en estas redes. Se afinará a medida que publiques.`;
  }
  return 'Todavía no tenemos datos tuyos: son los horarios que suelen funcionar en estas redes. Cuando publiques, se ajustarán a tu audiencia.';
}
